import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldCheck, RefreshCw, AlertCircle, Loader2, Timer } from 'lucide-react';
import { Button } from './Button';

interface OtpModalProps {
  isOpen: boolean;
  email: string;
  onClose: () => void;
  onVerify: (otp: string) => Promise<void>;
  onResend: () => Promise<{ success: boolean; retryAfter?: number }>;
  verifyError: string;
  isVerifying: boolean;
}

const OTP_EXPIRY_SECONDS = 10 * 60; // 10 minutes
const RESEND_COOLDOWN_SECONDS = 60;

export const OtpModal: React.FC<OtpModalProps> = ({
  isOpen,
  email,
  onClose,
  onVerify,
  onResend,
  verifyError,
  isVerifying,
}) => {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [expirySeconds, setExpirySeconds] = useState(OTP_EXPIRY_SECONDS);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDigits(['', '', '', '']);
      setExpirySeconds(OTP_EXPIRY_SECONDS);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendMessage('');
      setTimeout(() => inputRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  // OTP expiry countdown
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setExpirySeconds(s => {
        if (s <= 0) { clearInterval(timer); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Resend cooldown
  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => {
      setResendCooldown(s => {
        if (s <= 0) { clearInterval(timer); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  // Clear digits when verify error occurs so user can re-enter
  useEffect(() => {
    if (verifyError && !isVerifying) {
      setDigits(['', '', '', '']);
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    }
  }, [verifyError, isVerifying]);

  // Auto-trigger verify when all 4 digits filled
  const triggerVerify = useCallback(
    (newDigits: string[]) => {
      const otp = newDigits.join('');
      if (otp.length === 4 && /^\d{4}$/.test(otp) && !isVerifying) {
        onVerify(otp);
      }
    },
    [onVerify, isVerifying]
  );

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newDigits = [...digits];

    // Handle paste of full OTP
    if (value.length > 1) {
      const pastedDigits = value.slice(0, 4).split('');
      for (let i = 0; i < 4; i++) {
        newDigits[i] = pastedDigits[i] || '';
      }
      setDigits(newDigits);
      const lastFilled = Math.min(pastedDigits.length, 4) - 1;
      inputRefs.current[lastFilled]?.focus();
      triggerVerify(newDigits);
      return;
    }

    newDigits[index] = value;
    setDigits(newDigits);

    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }

    triggerVerify(newDigits);
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setResendMessage('');
    try {
      const result = await onResend();
      if (result.success) {
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
        setExpirySeconds(OTP_EXPIRY_SECONDS);
        setDigits(['', '', '', '']);
        setResendMessage('OTP resent successfully!');
        inputRefs.current[0]?.focus();
      } else if (result.retryAfter) {
        setResendCooldown(result.retryAfter);
      }
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isExpired = expirySeconds <= 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-navy-950/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-lg text-navy-400 hover:text-navy-700 hover:bg-navy-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="bg-gradient-to-br from-navy-900 to-navy-800 px-8 pt-8 pb-6 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent-500/20 flex items-center justify-center">
                <ShieldCheck className="w-8 h-8 text-accent-300" />
              </div>
              <h3 className="text-xl font-bold text-white mb-1">Verify Your Email</h3>
              <p className="text-navy-300 text-sm">
                We've sent a 4-digit code to
              </p>
              <p className="text-white font-medium text-sm mt-1">{email}</p>
            </div>

            {/* Body */}
            <div className="px-8 py-6">
              {/* Expiry Timer */}
              <div className="flex items-center justify-center gap-2 mb-6">
                <Timer className={`w-4 h-4 ${isExpired ? 'text-red-500' : expirySeconds < 60 ? 'text-amber-500' : 'text-navy-400'}`} />
                <span className={`text-sm font-mono font-medium ${isExpired ? 'text-red-500' : expirySeconds < 60 ? 'text-amber-500' : 'text-navy-500'}`}>
                  {isExpired ? 'OTP Expired' : `Expires in ${formatTime(expirySeconds)}`}
                </span>
              </div>

              {/* OTP Inputs */}
              <div className="flex justify-center gap-3 mb-6">
                {digits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={i === 0 ? 4 : 1}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    disabled={isVerifying || isExpired}
                    className={`w-14 h-16 text-center text-2xl font-bold rounded-xl border-2 outline-none transition-all
                      ${isVerifying ? 'bg-navy-50 border-navy-200 text-navy-400' : ''}
                      ${isExpired ? 'bg-red-50 border-red-200 text-red-400 cursor-not-allowed' : ''}
                      ${!isVerifying && !isExpired ? 'bg-white border-navy-200 text-navy-900 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20' : ''}
                      ${verifyError && !isVerifying ? 'border-red-400 bg-red-50' : ''}
                    `}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              {/* Loading indicator */}
              {isVerifying && (
                <div className="flex items-center justify-center gap-2 mb-4 text-accent-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm font-medium">Verifying...</span>
                </div>
              )}

              {/* Error */}
              {verifyError && !isVerifying && (
                <motion.div
                  className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-600">{verifyError}</p>
                </motion.div>
              )}

              {/* Resend success */}
              {resendMessage && (
                <motion.p
                  className="text-sm text-emerald-600 text-center mb-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {resendMessage}
                </motion.p>
              )}

              {/* Resend OTP */}
              <div className="text-center">
                <p className="text-sm text-navy-500 mb-3">Didn't receive the code?</p>
                {resendCooldown > 0 ? (
                  <p className="text-sm text-navy-400">
                    Resend available in <span className="font-mono font-medium text-navy-600">{formatTime(resendCooldown)}</span>
                  </p>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleResend}
                    isLoading={isResending}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Resend OTP
                  </Button>
                )}
              </div>
            </div>

            {/* Footer hint */}
            <div className="px-8 pb-6">
              <p className="text-xs text-navy-400 text-center">
                Check your spam folder if you don't see the email.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
